/**
 * Onboarding services (T027). Typed callable wrappers + an `onboard` orchestrator that runs the
 * US1 spine: Notion prefill → upsert Client → upsert Project → register Sources.
 */
import { callable } from './firebase';
import type {
  BillingIdentity,
  BillingSystem,
  CallableResult,
  Client,
  IdBasis,
  Project,
  SourceLink,
  SourceLinkKind,
} from '../types/entities';

export interface NotionMatch {
  notion_id: string;
  name: string;
  company: string | null;
  country: string | null;
  commercial_identifiers: Record<string, string>;
  deal_value?: number | null;
}

export const notionPrefill = callable<
  { query: string; kind: 'company' | 'deal' },
  { matches: NotionMatch[] }
>('notionPrefill');

export const upsertClient = callable<Partial<Client>, CallableResult<Client>>('upsertClient');
export const upsertProject = callable<Partial<Project>, CallableResult<Project>>('upsertProject');
export const upsertSourceLink = callable<Partial<SourceLink>, CallableResult<SourceLink>>(
  'upsertSourceLink',
);
export const upsertBillingIdentity = callable<Partial<BillingIdentity>, CallableResult<BillingIdentity>>(
  'upsertBillingIdentity',
);

export interface SourceLinkInput {
  kind: SourceLinkKind;
  external_id: string;
}

/** A billing entity to register for the client — one per legal entity/country (ADR-0001). */
export interface BillingIdentityInput {
  system: BillingSystem;
  external_id: string;
  billing_country: string;
  fiscal_id?: string | null;
  id_basis?: IdBasis;
}

export interface OnboardInput {
  client: Partial<Client>;
  project: Omit<Partial<Project>, 'client_id'> & { name: string; pm_tool: string };
  sources: SourceLinkInput[];
  billingIdentities?: BillingIdentityInput[];
}

export interface OnboardResult {
  client: Client;
  project: Project;
  sources: SourceLink[];
  billingIdentities: BillingIdentity[];
  warnings: string[];
}

/** What already succeeded, so a retry can resume instead of re-creating (avoids duplicate CC-NNN). */
export interface OnboardProgress {
  client?: Client;
  project?: Project;
  sourcesDone: number; // count of input.sources already registered
  billingDone?: number; // count of input.billingIdentities already registered
}

/** Thrown by onboard() on partial failure; carries the progress to resume from. */
export class OnboardError extends Error {
  constructor(
    message: string,
    readonly progress: OnboardProgress,
  ) {
    super(message);
    this.name = 'OnboardError';
  }
}

/**
 * Run the onboarding spine and return the created entities (SC-001). Resumable: pass the `resume`
 * progress from a prior failed attempt to skip already-created stages — so a retry never creates a
 * second client (`CC-NNN`). On any stage failure throws {@link OnboardError} carrying the progress.
 */
export async function onboard(input: OnboardInput, resume?: OnboardProgress): Promise<OnboardResult> {
  const warnings: string[] = [];
  const progress: OnboardProgress = {
    client: resume?.client,
    project: resume?.project,
    sourcesDone: resume?.sourcesDone ?? 0,
    billingDone: resume?.billingDone ?? 0,
  };
  try {
    if (!progress.client) {
      const res = await upsertClient(input.client);
      warnings.push(...res.warnings);
      progress.client = res.doc;
    }
    if (!progress.project) {
      const res = await upsertProject({ ...input.project, client_id: progress.client.canonical_client_id });
      warnings.push(...res.warnings);
      progress.project = res.doc;
    }
    const sources: SourceLink[] = [];
    for (let i = progress.sourcesDone; i < input.sources.length; i++) {
      const s = input.sources[i];
      const res = await upsertSourceLink({
        project_id: progress.project.project_id,
        kind: s.kind,
        external_id: s.external_id,
      });
      warnings.push(...res.warnings);
      sources.push(res.doc);
      progress.sourcesDone = i + 1;
    }
    // Billing identities — one per legal entity/country (ADR-0001). Belong to the client, so they only
    // need `progress.client`. Resumable via `billingDone` so a retry never re-creates one.
    const billingIdentities: BillingIdentity[] = [];
    const biInput = input.billingIdentities ?? [];
    for (let i = progress.billingDone ?? 0; i < biInput.length; i++) {
      const b = biInput[i];
      const res = await upsertBillingIdentity({
        client_id: progress.client.canonical_client_id,
        system: b.system,
        external_id: b.external_id,
        billing_country: b.billing_country,
        fiscal_id: b.fiscal_id ?? undefined,
        id_basis: b.id_basis,
      });
      warnings.push(...res.warnings);
      billingIdentities.push(res.doc);
      progress.billingDone = i + 1;
    }
    return { client: progress.client, project: progress.project, sources, billingIdentities, warnings };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Onboarding failed';
    throw new OnboardError(message, progress);
  }
}
